import type * as React from 'react'
import { forwardRef } from 'react'
import type { JSX } from 'react'
import type { ComponentPropsWithoutRef } from 'react'

import { clsx } from 'clsx'
import DOMPurify from 'dompurify'

import {
  Flex,
  Notice,
  Paragraph,
  ChannelIcon as PopcornChannelIcon,
} from '@buffer-mono/popcorn'
import type { ChannelIconProps as PopcornChannelIconProps } from '@buffer-mono/popcorn'

import type { Service } from '../../graphqlEnums'
import { getChannelColor } from '~publish/helpers/channels'

import styles from './ExpandedChannelSection.module.css'

type ExpandedChannelSectionRootProps = ComponentPropsWithoutRef<'div'> & {
  error?: string
}

const ExpandedChannelSectionRoot = forwardRef<
  HTMLDivElement,
  ExpandedChannelSectionRootProps
>(({ className, children, error, ...props }, ref) => {
  const htmlMessage = { __html: DOMPurify.sanitize(error ?? '') }
  return (
    <div
      className={clsx(styles.expandedChannelSection, className)}
      ref={ref}
      {...props}
    >
      {error && (
        <Notice variant="error" className={styles.notice}>
          <Paragraph dangerouslySetInnerHTML={htmlMessage}></Paragraph>
        </Notice>
      )}
      <div className={styles.contentWrapper}>{children}</div>
    </div>
  )
})

ExpandedChannelSectionRoot.displayName = 'ExpandedChannelSection.Root'

const ChannelIcon = ({
  className,
  service,
  channelCount,
  ...props
}: Omit<PopcornChannelIconProps, 'type'> & {
  service: Service
  channelCount?: number
}): JSX.Element => {
  const channelColor = getChannelColor(service)
  const style: React.CSSProperties & Record<string, string> = {
    '--channel-color': channelColor,
  }
  return (
    <Flex
      align="center"
      justify="center"
      className={clsx(styles.channelIconContainer, className)}
      style={style}
      data-service={service}
      data-channel-count={
        channelCount && channelCount > 1 ? channelCount : undefined
      }
    >
      <PopcornChannelIcon type={service} size="small" {...props} />
    </Flex>
  )
}

ChannelIcon.displayName = 'ExpandedChannelSection.ChannelIcon'

type ChannelContentProps = ComponentPropsWithoutRef<'div'>

const ChannelContent = ({
  className,
  ...props
}: ChannelContentProps): JSX.Element => {
  return <div className={clsx(styles.channelContent, className)} {...props} />
}

ChannelContent.displayName = 'ExpandedChannelSection.ChannelContent'

type ContentHeaderProps = ComponentPropsWithoutRef<'div'>

const ContentHeader = ({
  className,
  ...props
}: ContentHeaderProps): JSX.Element => {
  return <div className={clsx(styles.contentHeader, className)} {...props} />
}

ContentHeader.displayName = 'ExpandedChannelSection.ContentHeader'

type ContentBodyProps = ComponentPropsWithoutRef<'div'>

const ContentBody = ({
  className,
  ...props
}: ContentBodyProps): JSX.Element => {
  return <div className={clsx(styles.contentBody, className)} {...props} />
}

ContentBody.displayName = 'ExpandedChannelSection.ContentBody'

type ContentFooterProps = ComponentPropsWithoutRef<'div'>

const ContentFooter = ({
  className,
  ...props
}: ContentFooterProps): JSX.Element => {
  return <div className={clsx(styles.contentFooter, className)} {...props} />
}

ContentFooter.displayName = 'ExpandedChannelSection.ContentFooter'

export const ExpandedChannelSection = Object.assign(
  ExpandedChannelSectionRoot,
  {
    ChannelIcon,
    ChannelContent,
    ContentHeader,
    ContentBody,
    ContentFooter,
  },
)
